import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Lock, User } from 'lucide-react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import UserManagement from '@/features/user-management/components/UserManagement';
import PasswordReset from '@/features/user-management/components/PasswordReset';
import OrganizationSettings from '@/features/organization/components/OrganizationSettings';
import IntegrationsTab from '@/features/organization/components/IntegrationsTab';
import PurchaseOrderSettings from '@/features/organization/components/PurchaseOrderSettings';
import InventorySettings from '@/features/organization/components/InventorySettings';
import AgentApiSettings from '@/features/organization/components/AgentApiSettings';
import LocationManagement from '@/features/locations/components/LocationManagement';
import BillingTab from '@/features/billing/components/BillingTab';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/hooks/use-role';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { LanguageToggle } from '@/features/user-settings/components/LanguageToggle';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@/contexts/PageTitleContext';
import {
  ADMIN_SETTINGS_TAB_DESCRIPTORS,
  computeVisibleSettingsTabs,
  resolveActiveSettingsTab,
  type SettingsTabDescriptor,
} from './settingsTabs';

const MEMBER_SETTINGS_TABS = ['profile', 'security'] as const;

function getActiveTab<TTabs extends readonly string[]>(
  searchParams: URLSearchParams,
  tabs: TTabs,
  fallback: TTabs[number],
): TTabs[number] {
  const tab = searchParams.get('tab');
  if (tab && tabs.includes(tab as TTabs[number])) {
    return tab as TTabs[number];
  }
  return fallback;
}

export default function Settings() {
  const { profile } = useAuth();
  const { can } = useRole();
  const { t } = useTranslation('settings');
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = can('org_settings.manage');

  // Pure-client 3PL orgs get a reduced tab set (see settingsTabs.ts). Until the
  // verdict resolves isPureClient is false, so the conditional tab's slot is held
  // by a skeleton rather than rendered and yanked away.
  const { isPureClient, isLoading: surfacesLoading } = useOrgRoleSurfaces();
  // While loading, render only the tabs no org can lose, and hold the rest as
  // skeleton slots — so the strip never shows a client a tab it will lose, and
  // never leaves a TabsContent mounted with no trigger to label it.
  const visibleAdminTabs = surfacesLoading
    ? computeVisibleSettingsTabs(true)
    : computeVisibleSettingsTabs(isPureClient);
  // Render the strip in DESCRIPTOR order with a placeholder standing in the
  // conditional tab's own slot. Appending placeholders at the end instead would
  // shift every tab after Inventory (index 3) one column right when the verdict
  // lands — a visible jump for owner orgs, which are the common case.
  const adminTabSlots: Array<SettingsTabDescriptor | null> = surfacesLoading
    ? ADMIN_SETTINGS_TAB_DESCRIPTORS.map(tab => (tab.clientHidden ? null : tab))
    : visibleAdminTabs;
  const requestedTab = searchParams.get('tab');
  const activeAdminTab = resolveActiveSettingsTab(requestedTab, visibleAdminTabs, 'organization');
  const activeMemberTab = getActiveTab(searchParams, MEMBER_SETTINGS_TABS, 'profile');

  const handleTabChange = (tab: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams);
  };

  // A ?tab= pointing at a tab this org can't see (a stale bookmark, or a client
  // following an owner's link) resolves to Store — rewrite the URL to match so
  // refresh and back don't reproduce the mismatch.
  useEffect(() => {
    if (!isAdmin || surfacesLoading) return;
    if (!requestedTab || requestedTab === activeAdminTab) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', activeAdminTab);
    setSearchParams(nextParams, { replace: true });
  }, [isAdmin, surfacesLoading, requestedTab, activeAdminTab, searchParams, setSearchParams]);

  usePageTitle(t('title'), t('description'));

  return (
    <div className="flex flex-col">
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        {isAdmin ? (
          // Admin view - all tabs
          <Tabs value={activeAdminTab} onValueChange={handleTabChange} className="w-full">
            {/*
              Column count follows the visible tab count, so it must be an inline
              style: Tailwind can't extract an interpolated `grid-cols-${n}`, and
              this strip is the app's only consumer of the bare `grid-cols-8`
              utility — interpolating would drop the rule and stack the triggers.
              Mirrors features/production-issues/components/IssuesPage.tsx.
              Columns size to their label (`auto` shares the spare width); when nine
              tabs don't fit, the strip scrolls sideways instead of overlapping.
            */}
            <div className="max-w-6xl overflow-x-auto">
              <TabsList
                className="grid w-full min-w-max"
                style={{
                  gridTemplateColumns: `repeat(${adminTabSlots.length}, auto)`,
                }}
              >
                {adminTabSlots.map((tab, i) => {
                  // null = the conditional tab's reserved slot, held until the
                  // org-shape verdict lands rather than rendered and yanked away.
                  // Skeleton's default bg-muted is invisible on TabsList's own
                  // bg-muted trough, so it needs its own tone to read as loading.
                  if (!tab) {
                    return (
                      <Skeleton
                        key={`pending-tab-${i}`}
                        className="h-full w-full rounded-sm bg-muted-foreground/10"
                        aria-hidden="true"
                      />
                    );
                  }
                  const Icon = tab.icon;
                  return (
                    <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                      {/* Icons only where nine labels plus icons fit on one line. */}
                      <Icon className="hidden h-4 w-4 2xl:block" />
                      {t(tab.labelKey)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>

            <TabsContent value="organization" className="mt-6">
              <OrganizationSettings />
            </TabsContent>

            <TabsContent value="integrations" className="mt-6">
              <IntegrationsTab />
            </TabsContent>

            <TabsContent value="purchase-orders" className="mt-6">
              <PurchaseOrderSettings />
            </TabsContent>

            {visibleAdminTabs.some(tab => tab.id === 'inventory') && (
              <TabsContent value="inventory" className="mt-6">
                <InventorySettings />
              </TabsContent>
            )}

            <TabsContent value="locations" className="mt-6">
              <LocationManagement />
            </TabsContent>

            <TabsContent value="users" className="mt-6">
              <UserManagement />
            </TabsContent>

            <TabsContent value="security" className="mt-6">
              <div className="max-w-2xl space-y-6">
                <PasswordReset />
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-1">{t('common:languagePreference')}</h2>
                  <p className="text-sm text-muted-foreground mb-4">{t('common:languageDescription')}</p>
                  <div className="max-w-[200px]">
                    <LanguageToggle />
                  </div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="agent-api" className="mt-6">
              <AgentApiSettings />
            </TabsContent>

            {/* Mounted only when visible: a pure client never mounts it (no Stripe reads). */}
            {visibleAdminTabs.some(tab => tab.id === 'billing') && (
              <TabsContent value="billing" className="mt-6">
                <BillingTab />
              </TabsContent>
            )}
          </Tabs>
        ) : (
          // Non-admin view - only profile and security
          <div className="max-w-2xl">
            <Tabs value={activeMemberTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 max-w-md">
                <TabsTrigger value="profile" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('tabs.profile')}
                </TabsTrigger>
                <TabsTrigger value="security" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  {t('tabs.security')}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="profile" className="mt-6">
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">{t('profile.title')}</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('profile.name')}</label>
                      <p className="text-lg">{profile?.first_name} {profile?.last_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('profile.email')}</label>
                      <p className="text-lg">{profile?.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('profile.role')}</label>
                      <p className="text-lg">{profile?.role}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('profile.language')}</label>
                      <div className="mt-1 max-w-[200px]">
                        <LanguageToggle />
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="security" className="mt-6">
                <PasswordReset />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}
